import {BaseService, Service, StyleService} from '@frontapp/editor-html';
import {identity, isUndefined} from 'lodash';
import memoize from 'lru-memoize';
import {BehaviorSubject} from 'rxjs';
import {distinctUntilChanged, skip} from 'rxjs/operators';

import {formatFontFamily, parseFontFamily} from '../style/fontFamilyHelpers';
import {formatFontSize, parseFontSize} from '../style/fontSizeHelpers';
import {defaultHtmlStyle, HtmlStyle} from '../style/htmlStyle';

export interface HtmlStyleService extends Service {
  setDefaultStyle(defaultStyle: HtmlStyle): void;

  readonly style: HtmlStyle;
  applyStyle(style: HtmlStyle): void;
  resetStyle(): void;
}

export function createHtmlStyleService(
  styleService: StyleService,
  initialDefaultStyle?: HtmlStyle,
): HtmlStyleService {
  return new ConvertingHtmlStyleService(styleService, initialDefaultStyle);
}

export class ConvertingHtmlStyleService extends BaseService implements HtmlStyleService {
  constructor(styleService: StyleService, initialDefaultStyle: HtmlStyle = defaultHtmlStyle) {
    const defaultStyle$ = new BehaviorSubject(initialDefaultStyle);
    const defaultStyleService = {
      changes$: defaultStyle$.pipe(distinctUntilChanged(), skip(1)),
    };

    super(defaultStyleService, styleService);

    this.styleService = styleService;
    this.defaultStyle$ = defaultStyle$;
    this.parseFontFamilyMemoized = memoize(10)(parseFontFamily);
  }

  private readonly styleService: StyleService;
  private readonly defaultStyle$: BehaviorSubject<HtmlStyle>;
  private readonly parseFontFamilyMemoized: typeof parseFontFamily;

  private get defaultStyle() {
    return this.defaultStyle$.getValue();
  }

  setDefaultStyle(defaultStyle: HtmlStyle): void {
    this.defaultStyle$.next(defaultStyle);
  }

  get style() {
    const {defaultStyle} = this;
    const {style} = this.styleService;

    return {
      ...style,
      textColor: styleValueToHtmlStyleValue(style.textColor, defaultStyle.textColor),
      highlightColor: styleValueToHtmlStyleValue(style.highlightColor, defaultStyle.highlightColor),
      fontFamily: styleValueToHtmlStyleValue(
        style.fontFamily,
        defaultStyle.fontFamily,
        this.parseFontFamilyMemoized,
      ),
      fontSize: styleValueToHtmlStyleValue(style.fontSize, defaultStyle.fontSize, parseFontSize),
    };
  }

  applyStyle(style: HtmlStyle) {
    this.styleService.applyStyle({
      ...style,
      fontFamily: htmlStyleValueToStyleValue(style.fontFamily, formatFontFamily),
      fontSize: htmlStyleValueToStyleValue(style.fontSize, formatFontSize),
    });
  }

  resetStyle() {
    this.styleService.resetStyle();
  }
}

/**
 * Converts a Style value to an HtmlStyle value.
 *
 * - If the value is undefined (meaning mixed style), returns undefined ("—" indication).
 * - If the value is an empty string (meaning an absence of style), returns the default result (default style
 *   indication).
 * - Otherwise, tries to parse the string and returns either an HtmlStyle value or undefined (style indication
 *   or "—" if non-standard).
 */
function styleValueToHtmlStyleValue<T>(
  value: string | undefined,
  defaultResult: T | undefined,
  parser: (value: string) => T | undefined = identity,
): T | undefined {
  if (isUndefined(value)) {
    return undefined;
  }

  if (value === '') {
    return defaultResult;
  }

  return parser(value);
}

/** Converts an HtmlStyle value to a Style value. */
function htmlStyleValueToStyleValue<T>(
  value: T | undefined,
  formatter: (value: T) => string,
): string | undefined {
  if (isUndefined(value)) {
    return undefined;
  }

  return formatter(value);
}
